import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "Redux/hooks";
import { PBtn, PPill, PSelect, PText, fmtDate } from "./ui";
import { useSupplierCustomers } from "./shape-exceptions";

export default function CustomerPriceLists({ onEdit }: { onEdit: (customerId: number) => void }) {
  const { t } = useTranslation(["supplierPricing"]);
  const customLists = useAppSelector((s) => s.supplierPricing.customLists);
  const customers = useSupplierCustomers();
  const [q, setQ] = useState("");
  const [picker, setPicker] = useState("");
  const rows = customLists.filter((c) => (c.customerName || "").includes(q));
  const without = customers.filter((c) => !customLists.some((l) => l.customer === c.id));

  return (
    <>
      <div className="eb-pr-between eb-pr-customer-toolbar">
        <PText value={q} onChange={setQ} placeholder={t("customers.search")} width={320} />
        <div className="eb-pr-row">
          <PSelect
            width={240}
            value={picker}
            onChange={setPicker}
            ariaLabel={t("customers.customer")}
            options={[{ value: "", label: "—" }, ...without.map((c) => ({ value: c.id, label: c.name }))]}
          />
          <PBtn disabled={!picker} onClick={() => onEdit(Number(picker))}>
            {t("customers.add")}
          </PBtn>
        </div>
      </div>
      <div className="eb-pr-customer-cards">
        {rows.map((customer) => (
          <article key={customer.customer} className="eb-pr-customer-card">
            <header>
              <div>
                <strong>{customer.customerName}</strong>
                <PPill tone="blue">{t("customers.custom")}</PPill>
              </div>
              <PBtn kind="secondary" size="sm" onClick={() => onEdit(customer.customer)}>
                {t("customers.edit")}
              </PBtn>
            </header>
            <dl>
              <div><dt>{t("customers.discount")}</dt><dd>{customer.discountPercent ? customer.discountPercent + "%" : "—"}</dd></div>
              <div><dt>{t("customers.special")}</dt><dd>{customer.diameterOverrides.length ? t("customers.diametersCount", { count: customer.diameterOverrides.length }) : "—"}</dd></div>
              <div><dt>{t("customers.transport")}</dt><dd>{customer.transportMode === "custom" && customer.transportFreeAboveTons !== null ? t("transport.freeAbove") + " " + customer.transportFreeAboveTons + " " + t("units.ton") : t("customerTab.transportModes." + customer.transportMode)}</dd></div>
              <div><dt>{t("customers.updated")}</dt><dd>{fmtDate(customer.lastModified) || "—"}</dd></div>
            </dl>
          </article>
        ))}
        {rows.length === 0 && <p className="eb-pr-muted eb-pr-mobile-empty">—</p>}
      </div>
      <table className="eb-pr-table eb-pr-customer-table">
        <thead>
          <tr>
            <th>{t("customers.customer")}</th>
            <th>{t("customers.type")}</th>
            <th>{t("customers.discount")}</th>
            <th>{t("customers.special")}</th>
            <th>{t("customers.transport")}</th>
            <th>{t("customers.updated")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.customer}>
              <td>
                <b>{c.customerName}</b>
              </td>
              <td>
                <PPill tone="blue">{t("customers.custom")}</PPill>
              </td>
              <td>{c.discountPercent ? `${c.discountPercent}%` : "—"}</td>
              <td>{c.diameterOverrides.length ? t("customers.diametersCount", { count: c.diameterOverrides.length }) : "—"}</td>
              <td>
                {c.transportMode === "custom" && c.transportFreeAboveTons !== null
                  ? `${t("transport.freeAbove")} ${c.transportFreeAboveTons} ${t("units.ton")}`
                  : t(`customerTab.transportModes.${c.transportMode}`)}
              </td>
              <td>{fmtDate(c.lastModified)}</td>
              <td>
                <PBtn kind="secondary" size="sm" onClick={() => onEdit(c.customer)}>
                  {t("customers.edit")}
                </PBtn>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="eb-pr-muted">
                —
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="eb-pr-sub">{t("customers.rest")}</p>
    </>
  );
}
